import { useCallback, useEffect, useRef, useState } from "react";

const SPEECH_THRESHOLD = 0.022;   // RMS amplitude — above = "speech"
const SILENCE_THRESHOLD = 0.014;  // hysteresis — below = "silence"
const SILENCE_MS = 1100;           // ms of silence after speech = end of utterance
const MIN_CLIP_MS = 420;           // discard clips shorter than this

// Mic + RMS voice-activity detection. Calls onClip(blob) for each utterance while `armed()` is true.
export function useVoiceActivity({ armed, onClip, onSpeechStart, onError }) {
  const [waveform, setWaveform] = useState(0);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const speechStartRef = useRef(0);
  const lastLoudRef = useRef(0);
  const inSpeechRef = useRef(false);
  const cbRef = useRef({ armed, onClip, onSpeechStart, onError });
  useEffect(() => { cbRef.current = { armed, onClip, onSpeechStart, onError }; }, [armed, onClip, onSpeechStart, onError]);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch (err) { console.error("[ARIA]", err); }
    }
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (err) { console.error("[ARIA]", err); }
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    inSpeechRef.current = false;
    chunksRef.current = [];
  }, []);

  const beginClip = (stream) => {
    const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const dur = performance.now() - speechStartRef.current;
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      chunksRef.current = [];
      inSpeechRef.current = false;
      cbRef.current.onClip(dur < MIN_CLIP_MS || blob.size < 2000 ? null : blob);
    };
    rec.start();
    recorderRef.current = rec;
  };

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.fftSize);

      const tick = () => {
        if (!analyserRef.current) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length);
        setWaveform(Math.min(1, rms * 6));
        const now = performance.now();
        if (cbRef.current.armed()) {
          if (rms > SPEECH_THRESHOLD) {
            lastLoudRef.current = now;
            if (!inSpeechRef.current) {
              inSpeechRef.current = true;
              speechStartRef.current = now;
              try { beginClip(stream); cbRef.current.onSpeechStart?.(); }
              catch (err) { console.warn("recorder start failed", err); }
            }
          } else if (inSpeechRef.current && rms < SILENCE_THRESHOLD && now - lastLoudRef.current > SILENCE_MS) {
            if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      return true;
    } catch (e) {
      cbRef.current.onError?.(e);
      return false;
    }
  }, []);

  return { start, stop, waveform };
}
